import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../../../../services/api';
import { OrcamentoDetalhado } from '../../../../../types/orcamentos_multiplos';
import { toast } from 'react-toastify';
import ModalNovoOrcamento from './ModalNovoOrcamento';

interface OrcamentosTabProps {
    opportunityId: number;
    etapaAtual: number;
    funilAtual: number;
    formatCurrency: (value: number) => string;
    onUpdateComplete: () => void;
    // Muda quando outra aba altera orçamentos/produtos, para esta lista recarregar
    refreshKey?: number;
}

const OrcamentosTab: React.FC<OrcamentosTabProps> = ({
    opportunityId,
    etapaAtual,
    funilAtual,
    formatCurrency,
    onUpdateComplete,
    refreshKey = 0
}) => {
    const [orcamentos, setOrcamentos] = useState<OrcamentoDetalhado[]>([]);
    const [loading, setLoading] = useState(false);
    const jaCarregouRef = useRef(false);
    const [showModal, setShowModal] = useState(false);
    const [orcamentoSelecionado, setOrcamentoSelecionado] = useState<OrcamentoDetalhado | null>(null);

    // Carregar orçamentos da oportunidade
    useEffect(() => {
        if (opportunityId) {
            loadOrcamentos();
        }
    }, [opportunityId, refreshKey]);

    const loadOrcamentos = async () => {
        try {
            // Spinner só na primeira carga; nas recargas a lista é atualizada no lugar
            if (!jaCarregouRef.current) {
                setLoading(true);
            }
            const response = await apiService.getOrcamentos({
                id_oportunidade: opportunityId
            });

            if (response.success && Array.isArray(response.data)) {
                // Ordenar por versão (mais recente primeiro)
                const orcamentosOrdenados = response.data.sort((a, b) => b.versao - a.versao);
                setOrcamentos(orcamentosOrdenados);
            } else {
                setOrcamentos([]);
            }
        } catch (error: any) {
            console.error('Erro ao carregar orçamentos:', error);
            toast.error(error.message || 'Erro ao carregar orçamentos');
        } finally {
            jaCarregouRef.current = true;
            setLoading(false);
        }
    };

    const handleNovoOrcamento = () => {
        setOrcamentoSelecionado(null);
        setShowModal(true);
    };

    const handleEditarOrcamento = (orcamento: OrcamentoDetalhado) => {
        setOrcamentoSelecionado(orcamento);
        setShowModal(true);
    };

    const handleExcluirOrcamento = async (orcamento: OrcamentoDetalhado) => {
        if (!window.confirm(`Deseja realmente excluir o orçamento ${orcamento.numero_orcamento}?`)) {
            return;
        }

        try {
            await apiService.deleteOrcamento(orcamento.id);
            toast.success('Orçamento excluído com sucesso!');
            loadOrcamentos();
            onUpdateComplete();
        } catch (error: any) {
            console.error('Erro ao excluir orçamento:', error);
            toast.error(error.message || 'Erro ao excluir orçamento');
        }
    };

    const handleModalSave = () => {
        loadOrcamentos();
        onUpdateComplete();
    };

    return (
        <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                    <h4 className="fw-semibold mb-1">Orçamentos da Oportunidade</h4>
                    <p className="text-muted mb-0">
                        {orcamentos.length > 0
                            ? `${orcamentos.length} orçamento${orcamentos.length !== 1 ? 's' : ''} cadastrado${orcamentos.length !== 1 ? 's' : ''}`
                            : 'Nenhum orçamento cadastrado'
                        }
                    </p>
                </div>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleNovoOrcamento}
                >
                    <i className="ti ti-plus me-1" />
                    Novo Orçamento
                </button>
            </div>

            <div className="card-body">
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                        <p className="text-muted mt-2">Carregando orçamentos...</p>
                    </div>
                ) : orcamentos.length === 0 ? (
                    <div className="text-center py-5">
                        <div className="avatar avatar-xl bg-light-primary rounded-circle mb-3 mx-auto">
                            <i className="ti ti-files fs-36 text-primary" />
                        </div>
                        <h5 className="mb-2">Nenhum orçamento cadastrado</h5>
                        <p className="text-muted mb-3">
                            Comece criando um novo orçamento para esta oportunidade
                        </p>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleNovoOrcamento}
                        >
                            <i className="ti ti-plus me-1" />
                            Criar Primeiro Orçamento
                        </button>
                    </div>
                ) : (
                    <div className="row">
                        {orcamentos.map((orcamento) => (
                            <div key={orcamento.id} className="col-md-6 col-lg-4 mb-3">
                                <div className="card border h-100">
                                    <div className="card-header bg-light">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <h6 className="mb-0">{orcamento.numero_orcamento}</h6>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <h5 className="mb-3">{orcamento.titulo}</h5>

                                        <div className="mb-3">
                                            <small className="text-muted d-block">Fornecedor</small>
                                            <strong>
                                                {orcamento.fornecedor_nome || 'Não informado'}
                                            </strong>
                                        </div>

                                        <div className="mb-3">
                                            <small className="text-muted d-block">Valor Total</small>
                                            <h4 className="text-primary mb-0">
                                                {formatCurrency(parseFloat(orcamento.valor_total))}
                                            </h4>
                                        </div>

                                        <div className="mb-3">
                                            <small className="text-muted d-block">Produtos</small>
                                            <strong>{orcamento.total_produtos}</strong>
                                        </div>
                                    </div>
                                    <div className="card-footer bg-white">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted">
                                                {new Date(orcamento.data_criacao).toLocaleDateString('pt-BR')}
                                            </small>
                                            <div className="btn-group btn-group-sm">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handleEditarOrcamento(orcamento)}
                                                    title="Ver/Editar"
                                                >
                                                    <i className="ti ti-eye" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => handleEditarOrcamento(orcamento)}
                                                    title="Editar"
                                                >
                                                    <i className="ti ti-edit" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger"
                                                    onClick={() => handleExcluirOrcamento(orcamento)}
                                                    title="Excluir"
                                                >
                                                    <i className="ti ti-trash" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal de Novo/Editar Orçamento */}
            <ModalNovoOrcamento
                show={showModal}
                onHide={() => {
                    setShowModal(false);
                    setOrcamentoSelecionado(null);
                }}
                opportunityId={opportunityId}
                orcamento={orcamentoSelecionado}
                onSave={handleModalSave}
                formatCurrency={formatCurrency}
            />
        </div>
    );
};

export default OrcamentosTab;
